import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Search, X, Loader2, Sparkles, ArrowLeft, MessageCircle, Bookmark } from "lucide-react";
import { T, G, O, tr } from "../lib/i18n";
import AppBottomBar from "../components/AppBottomBar";
import { fetchRestaurant, fetchMenuFull, track } from "../lib/api";
import { LanguageSelector } from "../components/LanguageSelector";
import { CategoryNav, useActiveCategory } from "../components/CategoryNav";
import { DishCard } from "../components/DishCard";
import { SelectionSheet } from "../components/SelectionSheet";
import { QRDialog } from "../components/QRDialog";
import { Toaster } from "../components/ui/toaster";
import { RoomShortcut } from "../components/RoomShortcut";
import { MenuPill } from "../components/MenuPill";
import { toast } from "../hooks/use-toast";

export default function MenuPage() {
  const { menuId } = useParams();
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "it");
  const [q, setQ] = useState("");
  const [restaurant, setRestaurant] = useState(null);
  const [menu, setMenu] = useState(null); // the top-level menu
  const waOrder = !!(menu?.wa_order_enabled && menu?.wa_order_phone);
  const [categories, setCategories] = useState([]); // categories with items
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { localStorage.setItem("lang", lang); }, [lang]);

  const featuredDishes = useMemo(() => {
    const all = [];
    for (const cat of categories) for (const it of cat.items || []) if (it.featured) all.push(it);
    return all;
  }, [categories]);

  const showFeatured = featuredDishes.length > 0 && !q.trim();
  const [activeId, , lockActive] = useActiveCategory(categories, showFeatured);
  const headerRef = useRef(null);
  const scrollAnimRef = useRef(null);
  // Selection is SHARED across menus (user choice): guests browse freely and
  // send from any menu with WhatsApp ordering; staff decides what's available.
  const [selection, setSelection] = useState(() => {
    try { return JSON.parse(localStorage.getItem("selection") || "[]"); } catch { return []; }
  });
  const [sheetOpen, setSheetOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        const [r, mf] = await Promise.all([fetchRestaurant(), fetchMenuFull(menuId)]);
        if (!alive) return;
        setRestaurant(r);
        setMenu(mf.menu);
        setCategories(mf.categories);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message || "Errore");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [menuId]);

  useEffect(() => { localStorage.setItem("selection", JSON.stringify(selection)); }, [selection]);

  const filtered = useMemo(() => {
    if (!q.trim()) return categories;
    const qq = q.toLowerCase();
    return categories
      .map((cat) => ({
        ...cat,
        items: (cat.items || []).filter((it) => {
          const n = tr(it.name, lang).toLowerCase();
          const d = tr(it.description, lang).toLowerCase();
          return n.includes(qq) || d.includes(qq);
        })
      }))
      .filter((c) => c.items.length > 0);
  }, [q, lang, categories]);

  const totalResults = filtered.reduce((s, c) => s + (c.items?.length || 0), 0);

  const scrollToCategory = (id) => {
    const el = document.getElementById(`cat-${id}`);
    if (!el) return;
    const target = el.querySelector("h2") || el;
    const offset = () => { const nav = document.querySelector("[data-category-nav]"); return nav ? nav.getBoundingClientRect().bottom + 8 : 140; };
    const maxTop = () => document.documentElement.scrollHeight - window.innerHeight;
    const dest = () => Math.max(0, Math.min(target.getBoundingClientRect().top + window.scrollY - offset(), maxTop()));
    // Short rAF-driven animation (instant per-frame jumps, so the CSS
    // `scroll-behavior: smooth` can't stretch it): the destination is
    // re-read every frame, so a header that shrinks or an image that
    // loads mid-scroll can't make us land short or overshoot.
    if (scrollAnimRef.current) cancelAnimationFrame(scrollAnimRef.current);
    const start = window.scrollY, t0 = performance.now(), dur = 380;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    let cancelled = false;
    const cancel = () => { cancelled = true; if (scrollAnimRef.current) cancelAnimationFrame(scrollAnimRef.current); };
    window.addEventListener("wheel", cancel, { once: true, passive: true });
    window.addEventListener("touchstart", cancel, { once: true, passive: true });
    const step = (now) => {
      if (cancelled) return;
      const p = Math.min(1, (now - t0) / dur);
      window.scrollTo({ top: start + (dest() - start) * ease(p), behavior: "instant" });
      if (p < 1) { scrollAnimRef.current = requestAnimationFrame(step); return; }
      scrollAnimRef.current = null;
      setTimeout(() => { if (!cancelled && Math.abs(target.getBoundingClientRect().top - offset()) > 2) window.scrollTo({ top: dest(), behavior: "instant" }); }, 150);
    };
    scrollAnimRef.current = requestAnimationFrame(step);
  };

  useEffect(() => {
    const set = () => { if (headerRef.current) document.documentElement.style.setProperty("--menu-header-h", `${headerRef.current.offsetHeight}px`); };
    set();
    window.addEventListener("resize", set);
    return () => window.removeEventListener("resize", set);
  }, [loading]);

  const handleView = (dish) => track({ type: "dish_view", lang, menu_id: menuId, dish_id: dish.id });

  const handleAdd = (dish, price) => {
    track({ type: "dish_add", lang, menu_id: menuId, dish_id: dish.id });
    setSelection((prev) => {
      const idx = prev.findIndex((it) => it.dish.id === dish.id && it.price.label === price.label);
      if (idx >= 0) {
        const c = [...prev]; c[idx] = { ...c[idx], qty: c[idx].qty + 1 }; return c;
      }
      return [...prev, { dish, price, qty: 1 }];
    });
    toast({ title: `${tr(T.added, lang)}: ${tr(dish.name, lang)}` });
  };
  const handleRemove = (idx) => setSelection((prev) => prev.filter((_, i) => i !== idx));
  const totalItems = selection.reduce((s, it) => s + it.qty, 0);
  const totalPrice = selection.reduce((s, it) => s + it.price.value * it.qty, 0);
  const isSelected = (id) => selection.some((it) => it.dish.id === id);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7ebdf]">
        <div className="flex flex-col items-center gap-3 text-[#4a1c17]">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="text-sm">{tr(G.loading, lang)}</p>
        </div>
      </div>
    );
  }
  if (error || !menu) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7ebdf] p-4">
        <div className="text-center max-w-sm">
          <p className="font-serif text-xl text-[#4a1c17]">{tr(G.menuNotFound, lang)}</p>
          <p className="mt-2 text-sm text-[#7c5a4e]">{error || tr(G.menuMissing, lang)}</p>
          <button onClick={() => navigate("/")} className="mt-4 inline-flex items-center gap-1 rounded-full bg-[#4a1c17] text-[#f7ebdf] px-4 py-2 text-sm">
            <ArrowLeft className="h-4 w-4" /> {tr(G.backToMenus, lang)}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7ebdf]">
      {/* Top brand strip — sticky so the language picker + back arrow stay
          reachable while the customer scrolls through the menu. Uses z-40
          to sit above CategoryNav (z-30) so the tabs slide underneath. */}
      <div ref={headerRef} className="sticky top-0 z-40 bg-[#4a1c17] text-[#f7ebdf] shadow-md">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <RoomShortcut size="h-9 w-9" icon="h-4 w-4" />
          {restaurant?.logo && (
            <div
              aria-hidden="true"
              className="h-11 w-11 sm:h-16 sm:w-16 rounded-full bg-white flex items-center justify-center overflow-hidden shadow-sm shrink-0"
            >
              <img src={restaurant.logo} alt="" className="h-full w-full object-contain p-1" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-sans text-base sm:text-xl font-extrabold tracking-tight truncate">{tr(menu.name, lang)}</div>
            <div className="text-xs sm:text-sm opacity-75 truncate">{restaurant?.name}</div>
          </div>
          <MenuPill lang={lang} />
          <LanguageSelector lang={lang} setLang={setLang} onDark />
        </div>
      </div>

      {/* Search */}
      <div className="max-w-3xl mx-auto px-4 mt-4">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={tr(T.search, lang)}
            className="w-full pl-10 pr-9 py-3 rounded-full bg-white border border-[#e8dcc8] text-base focus:outline-none focus:ring-2 focus:ring-[#4a1c17]/30 focus:border-[#4a1c17]"
          />
          {q && (
            <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full hover:bg-neutral-100 flex items-center justify-center">
              <X className="h-3.5 w-3.5 text-neutral-500" />
            </button>
          )}
        </div>
        {q && (
          <div className="mt-2 text-xs text-[#7c5a4e]">
            {totalResults === 0 ? `${tr(T.noResults, lang)} "${q}"` : totalResults === 1 ? `${tr(T.oneResult, lang)} "${q}"` : `${totalResults} ${tr(T.nResults, lang)} "${q}"`}
          </div>
        )}
      </div>

      <div className="mt-4">
        <CategoryNav
          menu={categories}
          lang={lang}
          activeId={activeId}
          showFeatured={showFeatured}
          featuredLabel={tr({ it:"Menu del Giorno", en:"Today's", es:"Del Día", fr:"Du Jour", de:"Tagesmenü" }, lang)}
          onSelect={(id) => { lockActive(id, 900); scrollToCategory(id); }}
          stickyTop="var(--menu-header-h, 80px)"
        />

        <main className="max-w-3xl mx-auto px-4 pb-28 sm:pb-32">
          {showFeatured && (
            <section id="cat-__featured" className="pt-6 scroll-mt-20">
              <div className="rounded-2xl bg-gradient-to-br from-[#4a1c17] to-[#2b1410] text-[#f7ebdf] p-5 sm:p-6 shadow-lg relative overflow-hidden">
                <div className="absolute -top-8 -right-8 opacity-10"><Sparkles className="h-40 w-40" /></div>
                <div className="relative">
                  <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full bg-amber-300/20 text-amber-200 border border-amber-300/30">
                    <Sparkles className="h-3 w-3" /> {tr({ it:"Novità", en:"New", es:"Novedad", fr:"Nouveau", de:"Neu" }, lang)}
                  </div>
                  <h2 className="mt-3 font-serif text-2xl sm:text-3xl font-bold">
                    {tr({ it:"Menu del Giorno", en:"Today's Menu", es:"Menú del Día", fr:"Menu du Jour", de:"Tagesmenü" }, lang)}
                  </h2>
                </div>
              </div>
              <div className="mt-3 bg-white rounded-2xl border border-[#e8dcc8] px-4 sm:px-5 shadow-sm">
                {featuredDishes.map((dish) => (
                  <DishCard key={`f-${dish.id}`} dish={dish} lang={lang} onAdd={handleAdd} onView={handleView} isSelected={isSelected(dish.id)} />
                ))}
              </div>
            </section>
          )}

          {filtered.map((cat) => (
            <section key={cat.id} id={`cat-${cat.id}`} className="pt-4 scroll-mt-20">
              <h2 className="text-lg sm:text-xl font-bold text-[#2b1410] tracking-tight px-1">{tr(cat.name, lang)}</h2>
              {cat.subtitle && tr(cat.subtitle, lang) && (
                <p className="mt-0.5 text-sm text-[#7c5a4e] italic px-1">{tr(cat.subtitle, lang)}</p>
              )}
              <div className="mt-2 bg-white rounded-2xl border border-[#e8dcc8] px-4 sm:px-5 shadow-sm">
                {(cat.items || []).map((dish) => (
                  <DishCard key={dish.id} dish={dish} lang={lang} onAdd={handleAdd} onView={handleView} isSelected={isSelected(dish.id)} />
                ))}
              </div>
            </section>
          ))}
          {filtered.length === 0 && (
            <div className="py-20 text-center text-[#7c5a4e]">
              <p>{q ? `${tr(T.noResults, lang)} "${q}"` : tr({ it:"Nessun piatto in questo menu.", en:"No dishes in this menu.", es:"Sin platos.", fr:"Aucun plat.", de:"Keine Gerichte." }, lang)}</p>
            </div>
          )}
        </main>
      </div>

      {/* Bottom bar: shared app pills; WhatsApp-order menus get the green CTA row */}
      <AppBottomBar current="none" lang={lang} spacer={false} />

      {/* Floating selection button, only when something is selected: bordeaux "Appunti" or WhatsApp green "Invia ordine" */}
      {totalItems > 0 && (
        <button
          onClick={() => setSheetOpen(true)}
          data-testid="menu-selection-btn"
          aria-label={tr(waOrder ? O.sendOrder : O.notes, lang)}
          title={tr(waOrder ? O.sendOrder : O.notes, lang)}
          className={`fixed right-4 bottom-[4.25rem] sm:bottom-20 z-40 h-12 w-12 rounded-full shadow-lg ring-2 ring-white/80 inline-flex items-center justify-center active:scale-95 transition-[transform,background-color] animate-in fade-in zoom-in-75 duration-200 ${
            waOrder ? "bg-[#25D366] hover:bg-[#1ebe5b] text-white" : "bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf]"
          }`}
        >
          {waOrder ? <MessageCircle className="h-6 w-6" /> : <Bookmark className="h-5 w-5 text-[#C48B31]" />}
          <span data-testid="menu-selection-count" className={`absolute -top-1 -right-1 inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full text-[11px] font-bold ring-2 ring-white ${waOrder ? "bg-white text-emerald-700" : "bg-[#C48B31] text-[#2b1410]"}`}>{totalItems}</span>
        </button>
      )}

      <SelectionSheet open={sheetOpen} onOpenChange={setSheetOpen} items={selection} onRemove={handleRemove} onClear={() => setSelection([])} lang={lang} mode={waOrder ? "order" : "notes"} order={waOrder ? { phone: menu.wa_order_phone, menuName: tr(menu.name, "it"), placeLabel: menu.wa_order_place_label } : undefined} />
      <QRDialog open={qrOpen} onOpenChange={setQrOpen} lang={lang} restaurant={restaurant} />
      <Toaster />
    </div>
  );
}
